import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Card,
  Tag,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { proposalApi } from '../../services/proposal';
import type { Proposal } from '../../types/proposal';
import type { PaginationParams, FilterParams } from '../../types/api';
import { ProposalForm } from './ProposalForm';
import { ROUTES } from '../../config/routes';
import { PROPOSAL_STATUS_CONFIG, PROPOSAL_STATUS_OPTIONS } from '../../constants/proposalStatus';

const { Title } = Typography;

// 状态Tag颜色映射
const getStatusTag = (status: string) => {
  const config = PROPOSAL_STATUS_CONFIG[status] || { text: status, color: 'default' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

export const ProposalList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Proposal[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [customerFilter, setCustomerFilter] = useState<string | undefined>();
  const [formOpen, setFormOpen] = useState(false);
  const [editingProposalId, setEditingProposalId] = useState<string | undefined>();

  const fetchData = useCallback(async (params?: PaginationParams & FilterParams) => {
    setLoading(true);
    try {
      const response = await proposalApi.getProposals({
        page: params?.page || pagination.current,
        page_size: params?.page_size || pagination.pageSize,
        ...params,
      } as PaginationParams & FilterParams);
      // 后端返回格式: { data: T[], pagination: { total, page, page_size, total_pages } }
      setData(response.data || []);
      setPagination({
        current: response.pagination.page,
        pageSize: response.pagination.page_size,
        total: response.pagination.total,
      });
    } catch (error) {
      // 错误已在request拦截器中处理
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    fetchData({
      page: 1,
      query: searchKeyword,
      status: statusFilter,
      customer_id: customerFilter,
    });
  };

  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    fetchData({
      page: newPagination.current,
      page_size: newPagination.pageSize,
      query: searchKeyword,
      status: statusFilter,
      customer_id: customerFilter,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await proposalApi.deleteProposal(id);
      message.success('提案删除成功');
      fetchData({
        query: searchKeyword || undefined,
        status: statusFilter,
        customer_id: customerFilter,
      } as PaginationParams & FilterParams);
    } catch (error) {
      message.error('删除提案失败');
    }
  };

  const handleEdit = (id: string) => {
    setEditingProposalId(id);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditingProposalId(undefined);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    fetchData({
      query: searchKeyword,
      status: statusFilter,
      customer_id: customerFilter,
    });
  };

  const handleRowClick = (record: Proposal) => {
    navigate(`${ROUTES.PROPOSALS}/${record.id}`);
  };

  const columns = [
    {
      title: '提案标题',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Proposal) => (
        <a onClick={() => handleRowClick(record)}>{text}</a>
      ),
    },
    {
      title: '关联客户',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer: Proposal['customer']) => customer?.company_name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag,
    },
    {
      title: '创建者',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (text: string) => new Date(text).toLocaleDateString(),
      sorter: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      render: (_: unknown, record: Proposal) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(record);
            }}
          >
            查看
          </Button>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record.id);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除此提案吗？删除后不可恢复。"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record.id);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>提案管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          新建提案
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <Input.Search
            placeholder="搜索提案标题"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onSearch={handleSearch}
            enterButton={<><SearchOutlined /> 搜索</>}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="选择状态"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            onClear={handleSearch}
            onBlur={handleSearch}
            options={PROPOSAL_STATUS_OPTIONS}
            allowClear
            style={{ width: 120 }}
          />
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
      />

      <ProposalForm
        open={formOpen}
        proposalId={editingProposalId}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};
